import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { Search, X, Type, Hash, ToggleLeft } from 'lucide-react';
import Icon from '../../../shared/components/Icon';
import axiosInstance from '../../../shared/services/api/axiosInstance';
import {
  getSessionProperties,
  getSessionEventProperties,
  getSessionUserProperties,
  getDefaultSessionOperator
} from '../config/sessionFilterProperties';
import { getDefaultOperator } from '../config/transactionFilterProperties';

function getDataTypeIcon(type) {
  switch (type) {
    case 'string': return Type;
    case 'number': return Hash;
    case 'datetime':
    case 'date': return ({ color = "currentColor", size = 18 }) => <Icon name="calendar" size={size} color={color} />;
    case 'boolean': return ToggleLeft;
    case 'multiselect': return Type;
    default: return Type;
  }
}

/**
 * Session filter property picker: 4 tabs (Sessions, User properties, Event properties, All).
 * Same UX as TransactionPropertyFilterModule / Events global filter.
 */
const SessionPropertyFilterModule = ({
  isOpen,
  onClose,
  triggerRef,
  teamId,
  filters = [],
  existingFilter = null,
  onAdd = () => {},
}) => {
  const [selectedNavItem, setSelectedNavItem] = useState('sessions');
  const [searchTerm, setSearchTerm] = useState('');
  const [position, setPosition] = useState({ top: 0, left: 0 });
  const [userProperties, setUserProperties] = useState(() => getSessionUserProperties());
  const [loadingUserCustom, setLoadingUserCustom] = useState(false);
  const popupRef = useRef(null);

  const sessionProperties = getSessionProperties();
  const eventProperties = getSessionEventProperties();

  useEffect(() => {
    if (isOpen && triggerRef?.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      const scrollY = window.scrollY || window.pageYOffset;
      const scrollX = window.scrollX || window.pageXOffset;
      setPosition({
        top: rect.bottom + scrollY + 8,
        left: rect.left + scrollX,
      });
    }
  }, [isOpen, triggerRef]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (
        isOpen &&
        popupRef.current &&
        !popupRef.current.contains(e.target) &&
        triggerRef?.current &&
        !triggerRef.current.contains(e.target)
      ) {
        onClose();
      }
    };
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isOpen, onClose, triggerRef]);

  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && isOpen) onClose();
    };
    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      return () => document.removeEventListener('keydown', handleEscape);
    }
  }, [isOpen, onClose]);

  useEffect(() => {
    if (!isOpen || !teamId) return;
    let numericTeamId = typeof teamId === 'object' && teamId !== null
      ? parseInt(teamId._id || teamId.id, 10)
      : parseInt(teamId, 10);
    if (typeof teamId === 'string' && isNaN(numericTeamId)) {
      try {
        const teamDataStr = localStorage.getItem('selectedTeamData');
        if (teamDataStr) {
          const teamData = JSON.parse(teamDataStr);
          numericTeamId = parseInt(teamData._id || teamData.id, 10);
        }
      } catch (e) {
        numericTeamId = parseInt(teamId, 10);
      }
    }
    if (isNaN(numericTeamId) || !numericTeamId) return;
    setLoadingUserCustom(true);
    axiosInstance.get('/events/user-custom-properties', { params: { team_id: numericTeamId, limit: 5000 } })
      .then((res) => {
        if (res.data?.success && res.data.custom_properties) {
          const custom = res.data.custom_properties.map((prop) => {
            let propertyType = prop.type || 'string';
            const listItemType = propertyType.startsWith('list<') && propertyType.endsWith('>') ? propertyType.slice(5, -1) : null;
            if (listItemType) propertyType = 'list';
            return {
              name: `user.${prop.name}`,
              label: prop.name,
              type: propertyType,
              listItemType,
              source: 'user',
              isCustom: true,
            };
          });
          setUserProperties([...getSessionUserProperties(), ...custom]);
        }
      })
      .catch(() => setUserProperties(getSessionUserProperties()))
      .finally(() => setLoadingUserCustom(false));
  }, [isOpen, teamId]);

  if (!isOpen) return null;

  const allProperties = [...sessionProperties, ...userProperties, ...eventProperties];

  const getPropsForTab = () => {
    switch (selectedNavItem) {
      case 'sessions': return sessionProperties;
      case 'users': return userProperties;
      case 'events': return eventProperties;
      case 'all': return allProperties;
      default: return sessionProperties;
    }
  };

  const propsList = getPropsForTab();
  const filteredProps = propsList.filter(
    (p) =>
      (p.label || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      (p.name || '').toLowerCase().includes(searchTerm.toLowerCase())
  );

  const getCategory = (source) => {
    if (source === 'session') return 'session';
    if (source === 'user') return 'user';
    if (source === 'event') return 'event';
    return 'session';
  };

  const handleSelectProperty = (property) => {
    const category = getCategory(property.source);
    const isSession = category === 'session';
    const fieldOrProperty = isSession ? property.name : property.name;
    const operator = getDefaultSessionOperator(property.type);

    const newFilter = {
      id: existingFilter?.id || `session-filter-${Date.now()}-${Math.random().toString(36).slice(2)}`,
      category,
      propertySource: category,
      connector: existingFilter?.connector,
      field: isSession ? fieldOrProperty : undefined,
      property: isSession ? undefined : fieldOrProperty,
      propertyLabel: property.label,
      propertyType: property.type,
      operator,
      value: existingFilter && existingFilter.propertyType === property.type ? existingFilter.value : null,
      value2: existingFilter?.value2 ?? null,
    };
    onAdd(newFilter);
    onClose();
  };

  const tabs = [
    { id: 'sessions', label: 'Sessions' },
    { id: 'users', label: 'Users' },
    { id: 'events', label: 'Events' },
    { id: 'all', label: 'All' },
  ];

  return createPortal(
    <div
      ref={popupRef}
      className="fixed z-[9999] bg-white border border-gray-200 rounded-lg shadow-xl flex flex-col overflow-hidden"
      style={{
        top: `${position.top}px`,
        left: `${position.left}px`,
        width: '400px',
        maxHeight: '360px',
        fontFamily: 'Raleway, sans-serif',
      }}
    >
      <div className="px-3 py-2 border-b border-gray-200 flex-shrink-0">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#475569]" />
          <input
            type="text"
            placeholder="Search properties..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-9 pr-8 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1d0c46]"
          />
          {searchTerm && (
            <button
              type="button"
              onClick={() => setSearchTerm('')}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-[#475569] hover:text-[#475569]"
            >
              <X className="h-4 w-4" />
            </button>
          )}
        </div>
      </div>

      <div className="flex flex-1 min-h-0 overflow-hidden">
        <div className="w-24 border-r border-gray-200 bg-gray-50 flex-shrink-0 py-2">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              type="button"
              onClick={() => setSelectedNavItem(tab.id)}
              className={`w-full text-left px-2 py-1.5 text-xs transition-colors ${
                selectedNavItem === tab.id
                  ? 'bg-white border-l-2 border-[#4b57aa] text-[#4b57aa] font-semibold'
                  : 'text-[#475569] hover:bg-gray-100'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        <div className="flex-1 min-h-0 overflow-y-auto p-2">
          {loadingUserCustom && selectedNavItem !== 'sessions' && selectedNavItem !== 'events' ? (
            <div className="p-4 text-sm text-[#475569] text-center">Loading...</div>
          ) : filteredProps.length === 0 ? (
            <div className="p-4 text-sm text-[#475569] text-center">
              {searchTerm ? 'No properties match.' : 'No properties.'}
            </div>
          ) : (
            <div className="space-y-0.5">
              {filteredProps.map((prop, idx) => {
                const Icon = getDataTypeIcon(prop.type);
                const isSelected = existingFilter && (existingFilter.field === prop.name || existingFilter.property === prop.name) && getCategory(prop.source) === (existingFilter.category || existingFilter.propertySource);
                const badge = prop.source === 'user' ? 'U' : prop.source === 'event' ? 'E' : 'S';
                return (
                  <button
                    key={`${prop.source}-${prop.name}-${idx}`}
                    type="button"
                    onClick={() => handleSelectProperty(prop)}
                    className={`w-full text-left px-3 py-2 text-sm rounded transition-colors flex items-center gap-2 ${
                      isSelected ? 'bg-gray-200 border border-gray-300' : 'hover:bg-gray-50'
                    }`}
                  >
                    <Icon className="h-4 w-4 text-[#475569] flex-shrink-0" />
                    <span className="flex-1">{prop.label}</span>
                    <span
                      className="text-[10px] font-semibold uppercase w-5 h-5 inline-flex items-center justify-center rounded bg-gray-200 text-[#475569]"
                      title={prop.source}
                    >
                      {badge}
                    </span>
                    {isSelected && <span className="text-xs text-[#475569]">(current)</span>}
                  </button>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
};

export default SessionPropertyFilterModule;
export { getDataTypeIcon };
